// Wave 20.10 — comments + emoji reactions on Drop pages.
//
// Used by both SharedActivity (`/shared/:token`) and PeakDropPage
// (`/peak/:token`). Anonymous — no login. Identity per-browser via
// a client UUID stashed in localStorage so reactions de-dupe and
// the toggle-off gesture works.
//
// Disabled entirely when `expiresAt` is in the past — the UI shows
// a friendly "This drop has expired" state and the input controls
// are hidden. The server also enforces 410-on-expired if a stale
// client attempts a POST.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { colors } from '../theme';
// Wave 20.49al — Branded aero.chat glyphs for cheers on peak +
// session drops. Was `['🔥', '💪', '🤯', '🚴', '⚡']` — Unicode
// emojis that varied wildly by OS. The same custom SVG glyph
// set has powered ReplayView cheers since Wave 20.47b; this
// unifies all drop surfaces on one visual identity. Reactions
// from the pre-glyph era still render — DropInteractions falls
// back to raw display when the stored key isn't in
// CHEER_BY_KEY (see the rendering block below).
import { CHEER_PALETTE, CHEER_BY_KEY } from './cheerPalette';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const COMMENTER_KEY = 'aero.drops.commenterId';
const NICKNAME_KEY = 'aero.drops.nickname';
const ALLOWED = CHEER_PALETTE.map((c) => c.key);

function getCommenterId(): string {
  try {
    let id = localStorage.getItem(COMMENTER_KEY);
    if (!id) {
      // 24-char base36 — plenty for uniqueness, matches server shape check.
      id =
        Math.random().toString(36).slice(2, 14) +
        Math.random().toString(36).slice(2, 14);
      localStorage.setItem(COMMENTER_KEY, id);
    }
    return id;
  } catch {
    return 'anon';
  }
}

type DropType = 'peak' | 'activity';

interface Payload {
  reactions: Record<string, number>;
  myReactions: string[];
  comments: Array<{
    id: string;
    nickname: string;
    body: string;
    createdAt: string;
  }>;
}

interface Props {
  dropType: DropType;
  token: string;
  expiresAt: string | null;
}

export default function DropInteractions({ dropType, token, expiresAt }: Props) {
  const commenterId = useMemo(() => getCommenterId(), []);
  // Wave 20.22 — tick every second so isExpired flips at the
  // exact boundary and the countdown label under the header
  // stays live. Only mounts the interval when a deadline exists.
  const [, forceRerender] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => forceRerender((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const isExpired = !!expiresAt && new Date(expiresAt).getTime() < Date.now();
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nickname, setNickname] = useState<string>(() => {
    try {
      return localStorage.getItem(NICKNAME_KEY) || '';
    } catch {
      return '';
    }
  });
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const url = `${API}/public/drops/${dropType}/${encodeURIComponent(token)}`;

  const load = async () => {
    try {
      const res = await fetch(`${url}?commenterId=${encodeURIComponent(commenterId)}`);
      if (!res.ok) throw new Error(`load ${res.status}`);
      const d = (await res.json()) as Payload;
      setData(d);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  };

  useEffect(() => {
    load();
    // Poll every 15s so multiple people watching the same drop see
    // each other's reactions/comments appear roughly live.
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, commenterId]);

  const toggleReaction = async (emoji: string) => {
    if (isExpired || !data) return;
    const already = data.myReactions.includes(emoji);
    // Optimistic — update local state first, POST/DELETE, then reload.
    setData((prev) =>
      prev
        ? {
            ...prev,
            reactions: {
              ...prev.reactions,
              [emoji]: Math.max(0, (prev.reactions[emoji] ?? 0) + (already ? -1 : 1)),
            },
            myReactions: already
              ? prev.myReactions.filter((e) => e !== emoji)
              : [...prev.myReactions, emoji],
          }
        : prev,
    );
    try {
      if (already) {
        await fetch(
          `${url}/react?emoji=${encodeURIComponent(emoji)}&commenterId=${encodeURIComponent(commenterId)}`,
          { method: 'DELETE' },
        );
      } else {
        await fetch(`${url}/react`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emoji, commenterId }),
        });
      }
    } catch {
      // Silent — poll will resync counts.
    }
  };

  const submitComment = async () => {
    if (isExpired || !body.trim()) return;
    setSending(true);
    try {
      // Wave 20.22.1 — send Authorization when signed in so the
      // server's OptionalAuth guard swaps the client-sent nickname
      // for the signed-in user's own displayName/username. Anon
      // fallback: server defaults to "Anonymous" when no auth AND
      // client didn't provide anything.
      const label = nickname.trim() || 'Anonymous';
      const jwt = (() => {
        try {
          return localStorage.getItem(
            process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
          );
        } catch {
          return null;
        }
      })();
      const res = await fetch(`${url}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({ nickname: label, body: body.trim(), commenterId }),
      });
      if (!res.ok) throw new Error('post ' + res.status);
      setBody('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to post');
    } finally {
      setSending(false);
    }
  };

  if (error && !data) {
    return (
      <Box sx={{ mt: 4, p: 2, textAlign: 'center', color: colors.textMuted }}>
        Comments unavailable · {error}
      </Box>
    );
  }

  // Wave 20.49bc — Received-reactions summary. Rider reported
  // that reactions were "invisible" on their own drop links —
  // the counts lived as a small 0.85rem number tucked next to
  // one of eight palette buttons. Now the summary lives at the
  // top of the reactions block as a big, colored chip strip
  // that's the first thing your eye lands on, with an explicit
  // total so "3 total" reads at a glance.
  const brandedReceived = ALLOWED
    .map((key) => ({ key, def: CHEER_BY_KEY[key], count: data?.reactions?.[key] ?? 0 }))
    .filter((r) => r.count > 0);
  const legacyReceived = data
    ? Object.entries(data.reactions ?? {})
        .filter(([k, n]) => !CHEER_BY_KEY[k] && n > 0)
        .map(([k, n]) => ({ key: k, count: n as number }))
    : [];
  const totalReceived =
    brandedReceived.reduce((s, r) => s + r.count, 0) +
    legacyReceived.reduce((s, r) => s + r.count, 0);

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3, borderColor: colors.border }} />
      <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.15em' }}>
        Reactions {totalReceived > 0 ? `· ${totalReceived}` : ''}
      </Typography>

      {totalReceived > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 1, mb: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
        >
          {brandedReceived.map(({ key, def, count }) => {
            const Glyph = def?.Glyph;
            return (
              <Box
                key={`recv-${key}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.4,
                  py: 0.6,
                  borderRadius: 999,
                  backgroundColor: `${def?.color ?? colors.brand}22`,
                  border: `1px solid ${def?.color ?? colors.brand}66`,
                }}
                title={def?.label ?? key}
              >
                {Glyph ? <Glyph size={20} color={def!.color} /> : <span>{key}</span>}
                <span
                  style={{
                    color: colors.text,
                    fontSize: '0.95rem',
                    fontWeight: 800,
                  }}
                >
                  {count}
                </span>
              </Box>
            );
          })}
          {legacyReceived.map(({ key, count }) => (
            <Box
              key={`recv-legacy-${key}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.4,
                py: 0.6,
                borderRadius: 999,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>{key}</span>
              <span
                style={{
                  color: colors.text,
                  fontSize: '0.95rem',
                  fontWeight: 800,
                }}
              >
                {count}
              </span>
            </Box>
          ))}
        </Stack>
      )}

      <Typography
        variant="caption"
        sx={{ display: 'block', color: colors.textMuted, mt: 1, mb: 0.5 }}
      >
        {totalReceived > 0 ? 'Add your own cheer:' : 'Be the first to cheer:'}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 3, flexWrap: 'wrap', gap: 1 }}>
        {/* Wave 20.49al — Branded glyph picker for cheers.
            Each entry is a key from CHEER_PALETTE (aero, wind,
            summit, cadence, watts, focus, draft, goat); the
            button paints the SVG glyph in the palette color. */}
        {ALLOWED.map((key) => {
          const def = CHEER_BY_KEY[key];
          const count = data?.reactions?.[key] ?? 0;
          const isMine = data?.myReactions?.includes(key) ?? false;
          const Glyph = def?.Glyph;
          return (
            <Button
              key={key}
              onClick={() => toggleReaction(key)}
              disabled={isExpired}
              title={def?.label ?? key}
              sx={{
                minWidth: 0,
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                backgroundColor: isMine ? `${colors.brand}22` : colors.surface,
                border: `1px solid ${isMine ? colors.brand : colors.border}`,
                color: colors.text,
                textTransform: 'none',
                lineHeight: 1,
                '&:hover': {
                  backgroundColor: `${colors.brand}18`,
                  borderColor: colors.brand,
                },
                '&.Mui-disabled': {
                  opacity: 0.4,
                  color: colors.text,
                },
              }}
            >
              {Glyph ? (
                <Box sx={{ display: 'inline-flex', mr: count > 0 ? 0.75 : 0 }}>
                  <Glyph size={22} color={def!.color} />
                </Box>
              ) : (
                <span style={{ marginRight: 6 }}>{key}</span>
              )}
              {count > 0 && (
                <span style={{ fontSize: '0.85rem', fontWeight: 700, opacity: 0.85 }}>
                  {count}
                </span>
              )}
            </Button>
          );
        })}
        {/* Legacy Unicode reactions are surfaced in the top
            summary above; the picker below only offers the
            branded palette. */}
      </Stack>

      <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.15em' }}>
        Comments {data?.comments?.length ? `· ${data.comments.length}` : ''}
      </Typography>

      {isExpired ? (
        <Box sx={{ mt: 2, p: 2, borderRadius: 2, backgroundColor: colors.surface, border: `1px dashed ${colors.border}` }}>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
            This drop has expired. Reactions and comments are closed.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              placeholder="Say something…"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 280))}
              fullWidth
              inputProps={{ maxLength: 280 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && body.trim()) {
                  e.preventDefault();
                  submitComment();
                }
              }}
            />
            <Button
              variant="contained"
              disabled={sending || !body.trim()}
              onClick={submitComment}
              sx={{
                backgroundColor: colors.brand,
                color: colors.textInverse,
                fontWeight: 800,
                whiteSpace: 'nowrap',
              }}
            >
              Post
            </Button>
          </Stack>
        </Box>
      )}

      {data?.comments && data.comments.length > 0 ? (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {data.comments.map((c) => (
            <Box
              key={c.id}
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontWeight: 800, color: colors.text }}>
                  {c.nickname}
                </Typography>
                <Typography sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                  {relativeTime(c.createdAt)}
                </Typography>
              </Stack>
              <Typography sx={{ color: colors.text, whiteSpace: 'pre-wrap' }}>
                {c.body}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        !isExpired && (
          <Typography sx={{ mt: 2, color: colors.textMuted, fontStyle: 'italic', fontSize: '0.9rem' }}>
            Be the first to comment.
          </Typography>
        )
      )}
    </Box>
  );
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
